/* p78 · sepet kapısı (snippets/cd-cart-gate.liquid). Sepet sayfası ve çekmece aynı modülü kullanır.
   1) Sözleşme onayı: kutu sepet formuna bağlı ve required; işaretsizken "Ödemeye geç" düğmeleri tarayıcı denetimine
      takılır, burada tarayıcı balonu yerine anlaşılır hata (role=alert) verilir ve odak kutuya gider.
      Öznitelik YALNIZ iki durumda yazılır: kutu işaretlenince (yeni "Evet · <ISO tarih-saat>") ve işaret kaldırılınca ('').
      Sayfa yüklenince yalnız kutu sıfırlanır, sunucudaki öznitelik silinmez (ödeme sayfasından dönüp Geri ile ödemeye
      giden müşterinin onay kaydı kaybolmasın). Kutu required olduğu için sepetten ödemeye her geçişte yeniden işaretlenir.
      JS geç yüklenirken kutu zaten işaretlendiyse onay benimsenir ve yazılır. Sepet yeniden çizilince (morph) onay korunur.
      Hızlı ödeme düğmeleri (Shop Pay, Google Pay…) yalnız onay yazımı sunucuda başarıyla bitince görünür:
      kapıya data-consent-saved konur (CSS), tıklamaları da o zamana kadar burada engellenir.
   2) Hediye öznitelikleri: koşul Liquid'de hesaplanıp data-shaker / data-wheel-warn ile gelir. Sepet
      değişince (cart:lines-update / indirim olayları + yeniden çizimde değişen data-*) yalnız değişen öznitelikler tek
      istekle yazılır. "Çark uyarısı": çark sonucu duruyorken tutar eşiğin altına düşünce dolu, aksi halde boş.
      Eşik üstündeki ayrı hediye 15 Eki 2026'da kalktı: eski anahtarı (data-key-surprise) her zaman '' istenir, eski
      sepette değer kaldıysa bir kez silinir (Shopify boş değerli özniteliği siler), sonra istek gitmez.
   Yazmalar hediye çarkıyla (assets/cd-gift-wheel.js) ortak sırada gider: aynı sepete eşzamanlı /cart/update.js
   istekleri birbirinin özniteliğini ezmesin. */

import { Component } from '@theme/component';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';

/** @param {number} n */
const pad = (n) => String(n).padStart(2, '0');

/**
 * Yerel saat dilimli ISO 8601 (ör. 2026-10-14T15:42:07+03:00); sipariş panelinde Türkiye saatiyle okunur.
 * @param {Date} [date]
 */
function isoLocal(date = new Date()) {
  const offset = -date.getTimezoneOffset();
  const sign = offset >= 0 ? '+' : '-';
  const abs = Math.abs(offset);
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}` +
    `${sign}${pad(Math.floor(abs / 60))}:${pad(abs % 60)}`
  );
}

/* Sayfa ömrü boyunca tek durum (modül her sayfa yüklemesinde bir kez çalışır → onay sıfırdan başlar). */
const state = {
  /** '' = onay yok; yoksa "Evet · <ISO>" */
  consent: '',
  /** Bu sayfada kullanıcı kutuya dokundu mu; dokunmadıysa onay özniteliği yazılmaz (sunucudaki değer korunur) */
  consentTouched: false,
  /** Geçerli onay sunucuya yazıldı mı (hızlı ödeme düğmeleri bununla açılır) */
  consentSaved: false,
  /** Onay özniteliğinin anahtarı (ilk kapının data-key-consent'inden) */
  consentKey: '',
  /** @type {Record<string, string> | null} Sunucudaki son bilinen değerler: ilk çizimden, sonra yazma yanıtından */
  known: null,
};

/**
 * Sepet özniteliği yazmalarının sayfa genelindeki ortak sırası (assets/cd-gift-wheel.js aynı nesneyi kullanır).
 * Eski istek yenisini ezmesin, iki modül aynı anda yazmasın.
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueueAttributeWrite(task) {
  const queue = (globalThis.__cdCartAttrQueue ??= { tail: Promise.resolve() });
  const run = queue.tail.then(task, task);
  queue.tail = run.catch(() => {});
  return run;
}

/**
 * Değişen öznitelikleri tek istekte yazar. Boş değer özniteliği siler.
 * @param {Record<string, string>} changes
 */
function writeAttributes(changes) {
  const known = state.known ?? (state.known = {});
  /** @type {Record<string, string>} */
  const previous = {};
  for (const key of Object.keys(changes)) previous[key] = known[key] ?? '';
  // İyimser: yanıt gelmeden yeni bir eşitleme aynı isteği tekrar atmasın
  Object.assign(known, changes);

  enqueueAttributeWrite(async () => {
    try {
      const url = `${globalThis.Theme?.routes?.cart_update_url ?? '/cart/update'}.js`;
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify({ attributes: changes }) }));
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      for (const key of Object.keys(changes)) {
        if (known[key] === changes[key]) known[key] = cart?.attributes?.[key] ?? '';
      }
      // Onay yazıldı ve bu arada değişmedi → hızlı ödeme açılabilir
      const key = state.consentKey;
      if (key && key in changes && state.consent !== '' && changes[key] === state.consent) {
        if ((cart?.attributes?.[key] ?? '') === state.consent) {
          state.consentSaved = true;
          syncAll();
        }
      }
    } catch (error) {
      // Geri al: bir sonraki sepet değişikliğinde yeniden denenir
      for (const [key, value] of Object.entries(previous)) {
        if (known[key] === changes[key]) known[key] = value;
      }
      console.warn('[cd-cart-gate] Sepet özniteliği yazılamadı:', error);
    }
  });
}

/**
 * @typedef {object} Refs
 * @property {HTMLInputElement} [consentInput] - Onay kutusu
 * @property {HTMLElement} [consentError] - Hata metni (role=alert)
 *
 * @extends {Component<Refs>}
 */
class CdCartGate extends Component {
  /** @type {AbortController | null} */
  #abort = null;

  /* Yeniden çizimde hediye koşulu değişince (morph data-* günceller) ya da morph data-consent-saved'i silince eşitle */
  #observer = new MutationObserver(() => this.sync());

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('change', this.#onChange, { signal });
    // invalid kabarcıklanmaz; yakalama evresinde kutudan önce bu öğeye gelir
    this.addEventListener('invalid', this.#onInvalid, { capture: true, signal });
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-shaker', 'data-wheel-warn', 'data-consent-saved'],
    });
    if (!state.consentKey) state.consentKey = this.dataset.keyConsent ?? '';

    // Modül geç yüklendiyse müşteri kutuyu çoktan işaretlemiş olabilir: sessizce kaldırma, onayı benimse ve yaz
    const input = this.refs.consentInput;
    if (input instanceof HTMLInputElement && input.checked && !state.consent) {
      state.consent = `Evet · ${isoLocal()}`;
      state.consentTouched = true;
      state.consentSaved = false;
    }
    this.sync();
  }

  /* Sepet yeniden çizilince (morph): data-consent-saved ve aria-invalid durumu yeniden uygulanır */
  updatedCallback() {
    super.updatedCallback();
    this.sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#observer.disconnect();
  }

  /** Kutuyu sayfa durumuna getirir, değişen öznitelikleri yazar. */
  sync() {
    const { consentInput: input, consentError: error } = this.refs;
    if (input instanceof HTMLInputElement) {
      input.checked = state.consent !== '';
      input.value = state.consent || 'Evet';
      // aria-invalid hata metniyle birlikte yaşar: morph metni sildiyse (girişte data-skip-node-update var) kaldır
      const errorShown = error instanceof HTMLElement && error.textContent !== '';
      if (!errorShown || state.consent) input.removeAttribute('aria-invalid');
    }
    // Hızlı ödeme düğmeleri yalnız onay sunucuya yazılınca görünür (CSS: .cd-gate[data-consent-saved])
    this.toggleAttribute('data-consent-saved', state.consentSaved && state.consent !== '');

    const { keyConsent, keyShaker, keySurprise, keyWheelWarn } = this.dataset;
    if (!keyConsent || !keyShaker) return;

    if (!state.known) {
      state.known = {
        [keyConsent]: this.dataset.currentConsent ?? '',
        [keyShaker]: this.dataset.currentShaker ?? '',
      };
      if (keySurprise) state.known[keySurprise] = this.dataset.currentSurprise ?? '';
      if (keyWheelWarn) state.known[keyWheelWarn] = this.dataset.currentWheelWarn ?? '';
    }

    /** @type {Record<string, string>} */
    const wanted = {
      [keyShaker]: this.dataset.shaker ?? '',
    };
    // Kaldırılan eşik hediyesinin eski özniteliği: hep boş istenir, eski sepette kaldıysa silinir
    if (keySurprise) wanted[keySurprise] = '';
    if (keyWheelWarn) wanted[keyWheelWarn] = this.dataset.wheelWarn ?? '';
    // Onay yalnız kullanıcı bu sayfada kutuya dokunduysa yazılır; yüklemede sunucudaki değer silinmez
    if (state.consentTouched) wanted[keyConsent] = state.consent;
    /** @type {Record<string, string>} */
    const changes = {};
    for (const [key, value] of Object.entries(wanted)) {
      if ((state.known[key] ?? '') !== value) changes[key] = value;
    }
    if (Object.keys(changes).length > 0) writeAttributes(changes);
  }

  /** Onay yokken hata verir, odağı kutuya taşır. */
  showError() {
    const { consentInput: input, consentError: error } = this.refs;
    if (!(input instanceof HTMLInputElement)) return;

    if (error instanceof HTMLElement) {
      // Aynı metin tekrar duyurulsun diye önce boşalt, bir kare sonra yaz (o arada onay verildiyse yazma).
      // aria-invalid metinle aynı anda konur; sync() metin boşken onu kaldırır.
      error.textContent = '';
      input.removeAttribute('aria-invalid');
      requestAnimationFrame(() => {
        if (state.consent) return;
        error.textContent = this.dataset.error ?? '';
        input.setAttribute('aria-invalid', 'true');
      });
    } else {
      input.setAttribute('aria-invalid', 'true');
    }
    input.scrollIntoView({ block: 'center', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    input.focus({ preventScroll: true });
  }

  #clearError() {
    const { consentInput: input, consentError: error } = this.refs;
    input?.removeAttribute('aria-invalid');
    if (error instanceof HTMLElement) error.textContent = '';
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = this.refs.consentInput;
    if (!input || event.target !== input) return;

    state.consent = input.checked ? `Evet · ${isoLocal()}` : '';
    state.consentTouched = true;
    state.consentSaved = false; // yeni değer sunucuya yazılana kadar hızlı ödeme kapalı
    if (input.checked) this.#clearError();
    // Aynı sayfada başka kapı varsa hepsi aynı durumu göstersin; ortak "known" sayesinde yazma tek sefer gider
    syncAll();
  };

  /** @param {Event} event */
  #onInvalid = (event) => {
    if (event.target !== this.refs.consentInput) return;
    event.preventDefault(); // tarayıcı balonu yerine kendi mesajımız
    this.showError();
  };
}

if (!customElements.get('cd-cart-gate')) {
  customElements.define('cd-cart-gate', CdCartGate);
}

function syncAll() {
  for (const gate of document.querySelectorAll('cd-cart-gate')) {
    if (gate instanceof CdCartGate) gate.sync();
  }
}

/* Hızlı ödeme (Shop Pay, Google Pay…) tıklaması: onay sunucuya yazılmadıysa engelle. Gölge DOM'daki düğmenin olayı
   belgede host'a yönlenir; yakalama evresi düğmenin kendi işleyicisinden önce çalışır. iframe'li düğmeler CSS ile gizli. */
function guardExpressCheckout() {
  document.addEventListener(
    'click',
    (event) => {
      if (state.consent && state.consentSaved) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const express = target.closest('.additional-checkout-buttons');
      const gate = express?.parentElement?.querySelector('cd-cart-gate');
      if (!(gate instanceof CdCartGate)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      gate.showError();
    },
    true
  );
}

/* Horizon sepet olayları: işlem bitince eşitle. Yeniden çizim henüz gelmediyse data-* eski kalır ve istek gitmez;
   çizim gelince MutationObserver yeniden eşitler. */
async function listenCartEvents() {
  try {
    const { StandardEvents } = await import('@shopify/events');
    const names = [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate].filter(Boolean);
    for (const name of names) {
      document.addEventListener(name, (event) => {
        const promise = /** @type {{ promise?: Promise<unknown> }} */ (event).promise;
        Promise.resolve(promise)
          .catch(() => {})
          .finally(() => requestAnimationFrame(syncAll));
      });
    }
  } catch (error) {
    console.warn('[cd-cart-gate] standart olaylar yüklenemedi:', error);
  }
}

if (!window.__cdCartGateReady) {
  window.__cdCartGateReady = true;
  guardExpressCheckout();
  listenCartEvents();
}
